import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material'
import { useSearchParams } from 'react-router-dom'

import OrderSummaryCard from '../components/Orders/OrderSummaryCard'
import { PublicLink } from '../components/PublicLink'
import { buildPublicApiUrl } from '../config/api'
import { useCart } from '../hooks/useCart'
import { useOrders } from '../hooks/useOrders'
import { theme } from '../theme'
import { hasShortDisplayVariant, shortenOrderNumber } from '../utils/orderNumber'

const statusContent = {
  paid: {
    title: 'Plata a fost confirmată',
    description: 'Comanda ta a fost înregistrată cu succes și va intra în procesare.',
    severity: 'success'
  },
  payment_failed: {
    title: 'Plata nu a fost confirmată',
    description: 'Comanda rămâne salvată, dar plata a eșuat sau a fost întreruptă. Poți reveni în coș pentru o nouă încercare.',
    severity: 'error'
  },
  awaiting_payment: {
    title: 'Comanda a fost înregistrată',
    description: 'Plata este încă în curs de confirmare. Verificăm automat starea și actualizăm comanda imediat ce primim confirmarea.',
    severity: 'info'
  },
  refunded: {
    title: 'Plata a fost rambursată',
    description: 'Comanda este salvată în contul tău, iar plata a fost marcată ca rambursată.',
    severity: 'warning'
  }
}

const bankTransferView = (order) => {
  const status = order?.bank_transfer_status || 'awaiting_proof'
  if (order?.payment_status === 'paid' || status === 'confirmed') {
    return {
      title: 'Plata a fost confirmata',
      description: 'Plata prin ordin de plata a fost confirmata, iar comanda ta intra in procesare.',
      severity: 'success'
    }
  }
  if (status === 'proof_uploaded') {
    return {
      title: 'Dovada platii a fost trimisa',
      description: 'Am primit dovada platii si echipa noastra o verifica. Comanda ramane salvata in contul tau.',
      severity: 'info'
    }
  }
  if (status === 'rejected') {
    return {
      title: 'Dovada platii trebuie actualizata',
      description: 'Dovada platii a fost respinsa. Poti incarca un document nou din sumarul comenzii sau din Comenzile mele.',
      severity: 'warning'
    }
  }
  return {
    title: 'Comanda a fost inregistrata',
    description: 'Comanda a fost inregistrata si cosul s-a golit. Descarca proforma, plateste in contul afisat mai jos, foloseste numarul comenzii ca referinta si apoi incarca dovada platii. Comanda merge mai departe imediat ce plata este confirmata.',
    severity: 'info'
  }
}

const isValidStripeSessionId = (value) => Boolean(value && String(value).startsWith('cs_'))
const MAX_BACKGROUND_POLLS = 10
const BACKGROUND_POLL_INTERVAL_MS = 3000

const CheckoutSuccess = () => {
  const [searchParams] = useSearchParams()
  const { getOrder } = useOrders()
  const { getCartItems } = useCart()
  const orderId = searchParams.get('order')
  const orderNumber = searchParams.get('order_number')
  const displayOrderNumber = shortenOrderNumber(orderNumber)
  const sessionId = searchParams.get('session_id')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [order, setOrder] = useState(null)
  const [verificationState, setVerificationState] = useState('awaiting_payment')
  const paidCartSyncRef = useRef('')
  const pollAttemptsRef = useRef(0)

  const syncCartIfPaid = useCallback(async (nextOrder) => {
    if (!nextOrder?._id || nextOrder.payment_status !== 'paid') {
      return
    }

    if (paidCartSyncRef.current === nextOrder._id) {
      return
    }

    paidCartSyncRef.current = nextOrder._id
    try {
      await getCartItems(1)
    } catch (_error) {
      // Keep the page usable even if cart refresh fails.
    }
  }, [getCartItems])

  const syncOrder = useCallback(async ({ poll = false, background = false } = {}) => {
    const token = localStorage.getItem('token')
    if (!orderId || !token) {
      setLoading(false)
      setError('Nu am putut identifica această comandă.')
      return
    }

    if (poll && !background) {
      setLoading(true)
    } else {
      setLoading(true)
    }
    setError('')

    try {
      const orderPayload = await getOrder(orderId)
      const currentOrder = orderPayload.data || null
      if (currentOrder) {
        setOrder(currentOrder)
        setVerificationState(currentOrder.payment_status || 'awaiting_payment')
        await syncCartIfPaid(currentOrder)
      }

      if (currentOrder?.payment_method === 'bank_transfer') {
        setMessage('')
        return
      }

      const statusUrl = `${buildPublicApiUrl(`orders/${orderId}/checkout-status`)}${
        isValidStripeSessionId(sessionId) ? `?session_id=${encodeURIComponent(sessionId)}` : ''
      }`
      const response = await fetch(statusUrl, {
        headers: {
          Authorization: `${token}`,
          'Content-Type': 'application/json'
        }
      })
      const payload = await response.json()
      if (!response.ok) {
        throw new Error(payload.message || `Failed to check payment (${response.status})`)
      }

      if (payload.order) {
        setOrder(payload.order)
        setVerificationState(payload.order.payment_status || payload.payment_state || 'awaiting_payment')
        await syncCartIfPaid(payload.order)
      } else if (payload.payment_state) {
        setVerificationState(payload.payment_state)
      }
      setMessage(payload.message || '')
    } catch (syncError) {
      setError(syncError.message)
    } finally {
      setLoading(false)
    }
  }, [getOrder, orderId, sessionId, syncCartIfPaid])

  useEffect(() => {
    syncOrder()
  }, [syncOrder])

  useEffect(() => {
    if (!orderId) {
      return
    }

    if (order?.payment_method === 'bank_transfer') {
      pollAttemptsRef.current = 0
      return
    }

    if (verificationState !== 'awaiting_payment') {
      pollAttemptsRef.current = 0
      return
    }

    if (pollAttemptsRef.current >= MAX_BACKGROUND_POLLS) {
      return
    }

    const timer = window.setTimeout(() => {
      pollAttemptsRef.current += 1
      syncOrder({ poll: true, background: true })
    }, BACKGROUND_POLL_INTERVAL_MS)

    return () => {
      window.clearTimeout(timer)
    }
  }, [order?.payment_method, orderId, syncOrder, verificationState])

  const view = useMemo(() => {
    if (order?.payment_method === 'bank_transfer') {
      return bankTransferView(order)
    }
    const hasFinalInvoice = Boolean(order?.final_invoice?.download_url || order?.final_invoice_download_url)
    if (verificationState === 'paid' && order && !hasFinalInvoice) {
      return {
        title: 'Plata a fost confirmată',
        description: 'Comanda ta a fost înregistrată cu succes și intră în procesare. Proforma rămâne disponibilă în cont. Documentul Stripe este dovada plății, iar factura finală Primusvest va deveni disponibilă după procesare.',
        severity: 'success'
      }
    }
    if (verificationState === 'paid' && hasFinalInvoice) {
      return {
        title: 'Plata a fost confirmată',
        description: 'Comanda ta este în procesare, iar factura finală Primusvest este disponibilă în contul tău.',
        severity: 'success'
      }
    }
    return statusContent[verificationState] || statusContent.awaiting_payment
  }, [order, verificationState])

  return (
    <Box sx={{ minHeight: '80vh', bgcolor: theme.palette.secondary.main, padding: '40px 0' }}>
      <Box sx={{ maxWidth: 1100, margin: '0 auto', paddingX: 2 }}>
        <Stack spacing={3}>
          <Paper elevation={8} sx={{ padding: { xs: '24px', md: '32px' }, borderRadius: '18px' }}>
            <Stack spacing={2}>
              <Typography variant='h4' sx={{ fontSize: { xs: '26px', md: '32px' }, fontWeight: 700 }}>
                {view.title}
              </Typography>
              <Typography variant='body1'>
                {view.description}
              </Typography>
              {orderNumber && (
                <Stack spacing={0.25}>
                  <Typography variant='body1'>
                    <strong>Număr comandă:</strong> {displayOrderNumber}
                  </Typography>
                  {hasShortDisplayVariant(orderNumber) && (
                    <Typography variant='caption' sx={{ color: theme.palette.fontColor.medium }}>
                      Ref completă: {orderNumber}
                    </Typography>
                  )}
                </Stack>
              )}
              {message && <Alert severity={view.severity}>{message}</Alert>}
              {error && <Alert severity='warning'>{error}</Alert>}
              {verificationState === 'awaiting_payment' && !error && order?.payment_method !== 'bank_transfer' && (
                <Typography variant='body2' sx={{ color: theme.palette.fontColor.medium }}>
                  Verificăm automat starea plății în fundal timp de aproximativ 30 de secunde.
                </Typography>
              )}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <PublicLink to='/profilulmeu#Orders'>
                  <Button variant='outlined' color='accent' sx={{ borderRadius: '18px', minWidth: '240px' }}>
                    Vezi comenzile mele
                  </Button>
                </PublicLink>
                <PublicLink to='/'>
                  <Button variant='text' color='fontColor' sx={{ minWidth: '240px', justifyContent: 'flex-start' }}>
                    Continuă cumpărăturile
                  </Button>
                </PublicLink>
              </Stack>
            </Stack>
          </Paper>

          {loading
            ? (
              <Paper elevation={3} sx={{ padding: '24px', borderRadius: '18px' }}>
                <Typography>Se încarcă sumarul comenzii…</Typography>
              </Paper>
              )
            : order
              ? <OrderSummaryCard order={order} onOrderUpdated={setOrder} />
              : null}
        </Stack>
      </Box>
    </Box>
  )
}

export default CheckoutSuccess
